import { TextAlignment, TextVerticalAlignment } from "building-types/annotation/annotationTypesEnum";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import { sheetPointsNew } from "common/utils/nominalNumbers";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import type { Mutable } from "common/utils/types";
import { styleSheetMerge } from "doc/plans/planStyles";
import { resolveSheetGeometryStyle, typeSelector } from "doc/plans/primitives/primitiveStyles";
import type { GeometryStylesInternal } from "doc/plans/primitives/primitiveStylesTypes";
import type { SheetNode } from "lib/house/plans/sheetNode";
import { SheetNodeType } from "lib/house/plans/sheetNode";
import type { FontSizes } from "pdf/planFonts";
import type { SheetNodeStyleSheet, SheetNodeStylesInternal } from "./nodeStylesTypes";

const defaultSheetNodeStyles = (fontSizes: FontSizes) =>
  makeSheetNodeStyleSheet({
    /**
     * overrides for GeometrySheetNode primitives
     * Sheet based primitives are often aligned to top/left,
     * unlike their plan based counterparts
     */
    [SheetGeometryType.TEXT]: {
      verticalAlignment: TextVerticalAlignment.TOP,
      alignment: TextAlignment.LEFT,
      fontSize: fontSizes.regular,
      strokeWidth: ZERO_POINTS,
    },
    /**
     * SheetNode base styles
     */
    [SheetNodeType.TABLE]: {
      border: true,
      strokeColor: colors.black,
    },
    [SheetNodeType.TABLE_CELL]: {
      verticalAlignment: TextVerticalAlignment.TOP,
      alignment: TextAlignment.LEFT,
      border: true,
      strokeColor: colors.black,
      padding: sheetPointsNew(5),
      verticalPadding: ZERO_POINTS,
    },
  });

export function makeSheetNodeStyleSheet(
  props: Partial<GeometryStylesInternal & SheetNodeStylesInternal>,
): SheetNodeStyleSheet {
  return objectTypedKeys(props).reduce((styles, k: keyof typeof props) => {
    styles[typeSelector(k)] = props[k];
    return styles;
  }, {} as Mutable<SheetNodeStyleSheet>);
}

export function resolveSheetNodeStyle<T extends SheetNode>(
  node: T,
  fontSizes: FontSizes,
  stylesheet: SheetNodeStyleSheet = {},
): T {
  stylesheet = styleSheetMerge(defaultSheetNodeStyles(fontSizes), stylesheet);
  switch (node.type) {
    case SheetNodeType.TABLE_CELL: {
      if (node.contents === undefined) {
        return node;
      }
      const style = styleSheetMerge(stylesheet, node.style);
      const contents = resolveSheetNodeStyle(node.contents, fontSizes, style);
      return {
        ...node,
        contents,
        style,
      };
    }
    case SheetNodeType.TABLE: {
      const style = styleSheetMerge(stylesheet, node.style);
      const rows = node.rows.map((row) => row.map((cell) => resolveSheetNodeStyle(cell, fontSizes, style)));
      return {
        ...node,
        rows,
        style,
      };
    }
    case SheetNodeType.GEOMETRY: {
      const style = styleSheetMerge(stylesheet, node.style);
      return {
        ...node,
        geometry: resolveSheetGeometryStyle(node.geometry, style),
        style,
      };
    }
    default: {
      // currently do not have styles
      return node;
    }
  }
}

export function selectSheetNodeStyle<T extends keyof SheetNodeStylesInternal>(
  stylesheet: SheetNodeStyleSheet,
  nodeType: T,
): SheetNodeStylesInternal[T] {
  return stylesheet[typeSelector(nodeType)] ?? {};
}

/**
 * Utility type until all SheetNodes have an optional and similar style property
 */
interface StyledSheetNode<T extends keyof SheetNodeStylesInternal> {
  type: T;
  style?: SheetNodeStyleSheet;
}
export function sheetNodeGetStyle<T extends keyof SheetNodeStylesInternal>(
  node: StyledSheetNode<T>,
): SheetNodeStylesInternal[T] {
  return selectSheetNodeStyle(node.style ?? {}, node.type);
}
